import { useCallback, useMemo, useState } from 'react'
import {
    getHardwareCheckStatus,
    runHardwareCheck,
    swapWeighbridgePorts,
    confirmWeighbridgeAdapter,
} from '../../pages/generic/crud'
import { analyzeCheck, adaptersToConfirm } from './hardwareCheckAnalysis'
import { showToast, TOAST_SUCCESS, TOAST_FAILURE, TOAST_WARNING } from '../../utils/toast/Toast'

const errorMessage = (e, fallback) => (e && e.response && e.response.data && e.response.data.error) || fallback

/**
 * State for the Hardware Check button + modal. Loading status never touches the hardware;
 * only run() (the operator pressing Check / Recheck) reads the weighbridge ports.
 */
export function useHardwareCheck() {
    const [result, setResult] = useState(null)
    const [liveReadActive, setLiveReadActive] = useState(false)
    const [running, setRunning] = useState(false)
    const [swapping, setSwapping] = useState(false)
    const [busyMessage, setBusyMessage] = useState(null)

    const analysis = useMemo(() => analyzeCheck(result), [result])

    const refreshStatus = useCallback(async () => {
        try {
            const { data } = await getHardwareCheckStatus()
            setLiveReadActive(!!data.liveReadActive)
            if (data.lastResult) setResult(data.lastResult)
            if (!data.liveReadActive) setBusyMessage(null)
            return data
        } catch (e) {
            return null
        }
    }, [])

    const run = useCallback(async () => {
        setRunning(true)
        try {
            const { data } = await runHardwareCheck()
            setResult(data)
            setBusyMessage(null)
            setLiveReadActive(false)
            // Record the adapter serial number for groups that read fine on their current port,
            // so later reboots that reshuffle ttyUSB numbers are fixed automatically.
            adaptersToConfirm(analyzeCheck(data)).forEach((id) => confirmWeighbridgeAdapter(id).catch(() => {}))
        } catch (e) {
            if (e.response && e.response.status === 409) {
                setBusyMessage(errorMessage(e, 'A weight reading is in progress.'))
                setLiveReadActive(true)
            } else {
                showToast(errorMessage(e, 'Hardware Check failed'), TOAST_FAILURE)
            }
        } finally {
            setRunning(false)
        }
    }, [])

    const swap = useCallback(async (groupIdA, groupIdB) => {
        setSwapping(true)
        try {
            const { data } = await swapWeighbridgePorts(groupIdA, groupIdB)
            showToast(data.message || 'Weighbridge ports swapped', TOAST_SUCCESS)
            setSwapping(false)
            await run() // confirm the fix straight away
        } catch (e) {
            setSwapping(false)
            if (e.response && e.response.status === 409) {
                setBusyMessage(errorMessage(e, 'A weight reading is in progress.'))
                showToast(errorMessage(e, 'Cannot swap right now'), TOAST_WARNING)
            } else {
                showToast(errorMessage(e, 'Could not swap the weighbridge ports'), TOAST_FAILURE)
            }
        }
    }, [run])

    return { result, analysis, liveReadActive, busyMessage, running, swapping, refreshStatus, run, swap }
}
